/** Represents all possible units for a CSS "length" value. */
export type CSSLengthUnit =
  | 'cap'
  | 'ic'
  | 'lh'
  | 'rlh'
  | 'em'
  | 'ex'
  | '%'
  | 'px'
  | 'cm'
  | 'mm'
  | 'in'
  | 'pt'
  | 'Q'
  | 'pc'
  | 'ch'
  | 'rem'
  | 'vh'
  | 'vw'
  | 'vmin'
  | 'vmax'
  | 'svw'
  | 'lvw'
  | 'dvw'
  | 'svh'
  | 'lvh'
  | 'dvh'
  | 'svmax'
  | 'lvmax'
  | 'dvmax'
  | 'svmin'
  | 'lvmin'
  | 'dvmin'
  | 'vb'
  | 'vi'
  | 'svb'
  | 'lvb'
  | 'dvb'
  | 'svi'
  | 'lvi'
  | 'dvi';

/**
 * Represents a string which matches a valid CSS "length" value, such as "100%" or "25px".
 * @see https://developer.mozilla.org/en-US/docs/Web/CSS/length
 */
export type CSSLength<Length extends number = number> =
  | `${Length}${CSSLengthUnit}`
  | `0`;

/**
 * Represents a string corresponds to a CSS "length" value in pixels, such as "25px".
 * @see https://developer.mozilla.org/en-US/docs/Web/CSS/length
 */
export type CSSPixelLength<Length extends number = number> =
  | `${Length}px`
  | `0`;

/**
 * Defines how the content of a _replaced element_, such as an `<img>` or `<video>`
 * should be resized to fit its container.
 * @see https://developer.mozilla.org/en-US/docs/Web/CSS/object-fit
 */
export type CSSObjectFit = 'contain' | 'cover' | 'fill' | 'none' | 'scale-down';

/**
 * Represents a "spacing" value, as understood by the application.
 * @see {@link ../../styles/variables/_spacing.scss}
 */
export type StyleSpacing =
  | 'xxxxs'
  | 'xxxs'
  | 'xxs'
  | 'xs'
  | 'sm'
  | 'base'
  | 'md'
  | 'lg'
  | 'xl'
  | 'xxl'
  | 'xxxl'
  | 'xxxxl';

/**
 * Represents any acceptable value for a CSS "length"-like value. This includes
 * numbers, number-like strings, "spacing" values, and CSS "length" values. When a
 * value is provided as a number or number-like string without a unit, it is presumed
 * that the number presents a pixel value.
 * @example ```ts
 * const vals: Array<AnyAcceptableCSSLength> = [
 *  16,
 *  '16',
 *  '16px',
 *  '16%',
 *  'sm'
 * ]; // All provided values are acceptable.
 * ```
 */
export type AnyAcceptableCSSLength<Length extends number = number> =
  | Length
  | `${Length}`
  | StyleSpacing
  | CSSLength<Length>;

/**
 * Represents a CSS module, which is a record of class names that map to style rules.
 *
 * @see {@link https://github.com/css-modules/icss Interoperable CSS (ICSS) }
 */
export interface ICSSModule {
  readonly [key: string]: string;
}

/**
 * Defines global CSS custom properties used throughout the application.
 * These are associated with SCSS variables defined in the layout styles, and
 * are intended to be set at the `:root` level for global access.
 */
export enum GlobalCSSCustomProperty {
  /**
   * The height of the site header (mobile and desktop) in pixels.
   * @see $header-height SCSS variable.
   */
  HeaderHeight = '--header-height',
  /**
   * The height of the site header on desktop displays in pixels.
   * @see $header-height-desktop SCSS variable.
   */
  HeaderHeightDesktop = '--header-height-desktop',
  /**
   * The height of the header utility bar in pixels.
   * @see $header-utility-bar-height SCSS variable.
   */
  HeaderUtilityBarHeight = '--header-utility-bar-height',
  /**
   * The top offset of the header utility bar in pixels.
   * @see $header-utility-bar-top SCSS variable.
   */
  HeaderUtilityBarTop = '--header-utility-bar-top',
  /**
   * The total height of the site header, including the utility bar, in pixels.
   * @see $header-total-height SCSS variable.
   */
  HeaderTotalHeight = '--header-total-height'
}
